import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Reveal, { Eyebrow } from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";
import ArtReel from "@/components/ArtReel";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);
  const [videoRatio, setVideoRatio] = useState(16 / 9);

  useEffect(() => {
    setProject(null);
    setError(false);
    api.get(`/portfolio/${slug}`).then((r) => {
      setProject(r.data);
      if (r.data.aspect) {
        const [a, b] = r.data.aspect.split("/").map(Number);
        if (a && b) setVideoRatio(a / b);
      }
    }).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="pt-40 pb-32 text-center" data-testid="project-not-found">
        <h1 className="font-display text-3xl font-bold">Project not found</h1>
        <Link to="/portfolio" className="mt-4 inline-block text-arsenal hover:underline">Back to portfolio</Link>
      </div>
    );

  if (!project)
    return <div className="pt-40 pb-32 text-center text-slate-500" data-testid="project-loading">Loading project…</div>;

  return (
    <div data-testid="project-detail-page" className="pt-24 sm:pt-28">
      <Seo title={project.title} description={project.description} path={`/portfolio/${slug}`} image={`/og/portfolio-${slug}.png`} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/portfolio" data-testid="project-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-arsenal transition-colors">
          <ArrowLeft className="h-4 w-4" /> ALL WORK
        </Link>

        <header className="mt-8 pb-10">
          <div className="flex flex-wrap gap-2 mb-4">
            {project.categories.map((c) => (
              <span key={c} className="clip-tag bg-arsenal/90 px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-white">{c.toUpperCase()}</span>
            ))}
            {project.sample && <span className="clip-tag bg-white/10 px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-slate-300">CONCEPT SHOWCASE</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-balance">{project.title}</h1>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-400">
            <span><span className="text-slate-600">Client —</span> {project.client}</span>
            <span><span className="text-slate-600">Date —</span> {project.date}</span>
          </div>
        </header>
      </div>

      <Reveal>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="overflow-hidden border border-white/10">
            <img
              src={project.cover}
              alt={project.title}
              className="w-full max-h-[70vh] object-cover"
              style={{ animation: "kenburns 20s ease-in-out infinite alternate" }}
              data-testid="project-cover"
            />
          </div>
        </div>
      </Reveal>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <div className="lg:sticky lg:top-28">
            <Eyebrow>ABOUT THE PROJECT</Eyebrow>
            <p className="text-slate-300 leading-relaxed">{project.description}</p>
            <Link
              to="/start-a-project"
              data-testid="project-start-similar"
              className="clip-btn mt-8 inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors"
            >
              START SOMETHING LIKE THIS <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
        <div className="space-y-6">
          {project.before_after && (
            <Reveal>
              <BeforeAfter before={project.before_after.before} after={project.before_after.after} alt={project.title} />
            </Reveal>
          )}
          {project.video_url && (
            <Reveal>
              <div className="flex justify-center">
                <div
                  className={`border border-white/10 bg-black ${videoRatio < 1 ? "mx-auto" : "w-full"}`}
                  style={videoRatio < 1 ? { aspectRatio: `${videoRatio}`, height: "min(72vh, 640px)" } : { aspectRatio: `${videoRatio}` }}
                >
                  <video
                    poster={project.cover}
                    controls
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      if (v.videoWidth && v.videoHeight) setVideoRatio(v.videoWidth / v.videoHeight);
                    }}
                    className="h-full w-full object-contain"
                    data-testid="project-video"
                  >
                    <source src={project.video_url.replace(".mp4", ".webm")} type="video/webm" />
                    <source src={project.video_url} type="video/mp4" />
                  </video>
                </div>
              </div>
            </Reveal>
          )}
          {project.gallery?.length <= 2 &&
            project.gallery?.filter((g) => g !== project.cover).map((g, i) => (
              <Reveal key={g} delay={i * 0.05}>
                <div className="overflow-hidden border border-white/10">
                  <img src={g} alt={`${project.title} — gallery image ${i + 1}`} loading="lazy" className="w-full object-cover" />
                </div>
              </Reveal>
            ))}
        </div>
      </div>

      {project.gallery?.length > 2 && (
        <div className="border-t border-white/5 bg-ink-surface/30">
          <ArtReel images={project.gallery} title={project.title} />
        </div>
      )}

      {project.related?.length > 0 && (
        <section className="border-t border-white/5 bg-ink-surface/40">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
            <Eyebrow>RELATED WORK</Eyebrow>
            <div className="mt-8 grid sm:grid-cols-3 gap-6">
              {project.related.map((r) => (
                <Link key={r.id} to={`/portfolio/${r.slug}`} data-testid={`related-${r.slug}`} className="group relative block overflow-hidden border border-white/10">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={r.cover} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                  <h3 className="absolute bottom-0 p-5 font-display text-base font-bold text-white">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
